import {
	Badge,
	Button,
	Flex,
	Input,
	NumberDecrementStepper,
	NumberIncrementStepper,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	Switch,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import Card from '../../../components/card/Card';
import { LiveLeadClientRow } from '../../../models/liveLead.model';
import {
	fetchLiveLeadClients,
	saveLiveLeadClient
} from '../../../store/features/liveLead/liveLead.slice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { RootState } from '../../../store/store';

/**
 * Visão do admin: todos os clientes, o volume de cada um e o botão que liga e
 * desliga a entrega conta a conta, a qualquer momento. Desligar não cancela o
 * que já saiu — apenas para de gerar entrega nova.
 */
export default function ClientesAdmin() {
	const dispatch = useAppDispatch();
	const clients = useAppSelector((state: RootState) => state.liveLead.clients);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');

	// Edição local por linha: só o que o admin mexeu é enviado ao salvar.
	const [draft, setDraft] = useState<Record<string, Partial<LiveLeadClientRow>>>({});

	useEffect(() => {
		dispatch(fetchLiveLeadClients());
	}, [dispatch]);

	const value = <K extends keyof LiveLeadClientRow>(row: LiveLeadClientRow, field: K) =>
		(draft[row.userId]?.[field] ?? row[field]) as LiveLeadClientRow[K];

	const setField = <K extends keyof LiveLeadClientRow>(
		userId: string,
		field: K,
		next: LiveLeadClientRow[K]
	) => setDraft(d => ({ ...d, [userId]: { ...d[userId], [field]: next } }));

	const save = (userId: string) => {
		const changes = draft[userId];
		if (!changes || Object.keys(changes).length === 0) return;
		dispatch(saveLiveLeadClient({ userId, changes }));
		setDraft(d => ({ ...d, [userId]: {} }));
	};

	return (
		<Card p='20px'>
			<Text fontSize='lg' fontWeight='bold' color={textColor} mb='4px'>
				Clientes
			</Text>
			<Text fontSize='sm' color={subColor} mb='16px'>
				O atraso é o tempo entre o lead entrar na base e sair para o cliente. O intervalo espaça as
				entregas (cascata); o teto por hora limita o volume. 0 = sem limite. Leads/chamada acima de 1
				manda os leads em lote (até 100 por POST) — só ligue depois que o cliente aceitar o formato.
			</Text>

			{clients.length === 0 ? (
				<Text fontSize='sm' color={subColor}>
					Nenhum cliente cadastrado.
				</Text>
			) : (
				<Table size='sm' variant='simple'>
					<Thead>
						<Tr>
							<Th>Cliente</Th>
							<Th>Ativo</Th>
							<Th>Webhook</Th>
							<Th isNumeric>Atraso (min)</Th>
							<Th isNumeric>Intervalo (s)</Th>
							<Th isNumeric>Máx/hora</Th>
							<Th isNumeric>Leads/chamada</Th>
							<Th isNumeric>24h</Th>
							<Th isNumeric>Total</Th>
							<Th></Th>
						</Tr>
					</Thead>
					<Tbody>
						{clients.map(row => {
							const dirty = Object.keys(draft[row.userId] || {}).length > 0;
							return (
								<Tr key={row.userId}>
									<Td>
										<Text fontSize='sm' fontWeight='500' color={textColor}>
											{row.name}
										</Text>
										<Text fontSize='xs' color={subColor}>
											{row.email}
										</Text>
									</Td>
									<Td>
										<Switch
											isChecked={value(row, 'enabled') as boolean}
											onChange={e =>
												dispatch(
													saveLiveLeadClient({
														userId: row.userId,
														changes: { enabled: e.target.checked }
													})
												)
											}
										/>
									</Td>
									<Td minW='220px'>
										<Input
											size='xs'
											placeholder='https://…'
											value={(value(row, 'webhookUrl') as string) || ''}
											onChange={e => setField(row.userId, 'webhookUrl', e.target.value)}
										/>
										{Object.keys(row.webhookHeaders || {}).length > 0 ? (
											<Text fontSize='10px' color={subColor} mt='2px'>
												{Object.keys(row.webhookHeaders).join(', ')}
											</Text>
										) : (
											<Text fontSize='10px' color={subColor} mt='2px'>
												sem header de autenticação
											</Text>
										)}
									</Td>
									<Td isNumeric maxW='110px'>
										<NumberInput
											size='xs'
											min={0}
											max={1440}
											value={value(row, 'delayMinutes') as number}
											onChange={(_, n) => setField(row.userId, 'delayMinutes', Number.isNaN(n) ? 0 : n)}
										>
											<NumberInputField />
											<NumberInputStepper>
												<NumberIncrementStepper />
												<NumberDecrementStepper />
											</NumberInputStepper>
										</NumberInput>
									</Td>
									<Td isNumeric maxW='110px'>
										<NumberInput
											size='xs'
											min={0}
											max={3600}
											value={value(row, 'spacingSeconds') as number}
											onChange={(_, n) => setField(row.userId, 'spacingSeconds', Number.isNaN(n) ? 0 : n)}
										>
											<NumberInputField />
											<NumberInputStepper>
												<NumberIncrementStepper />
												<NumberDecrementStepper />
											</NumberInputStepper>
										</NumberInput>
									</Td>
									<Td isNumeric maxW='110px'>
										<NumberInput
											size='xs'
											min={0}
											value={value(row, 'maxPerHour') as number}
											onChange={(_, n) => setField(row.userId, 'maxPerHour', Number.isNaN(n) ? 0 : n)}
										>
											<NumberInputField />
											<NumberInputStepper>
												<NumberIncrementStepper />
												<NumberDecrementStepper />
											</NumberInputStepper>
										</NumberInput>
									</Td>
									<Td isNumeric maxW='110px'>
										<NumberInput
											size='xs'
											min={1}
											max={100}
											value={value(row, 'batchSize') as number}
											onChange={(_, n) => setField(row.userId, 'batchSize', Number.isNaN(n) ? 1 : n)}
										>
											<NumberInputField />
											<NumberInputStepper>
												<NumberIncrementStepper />
												<NumberDecrementStepper />
											</NumberInputStepper>
										</NumberInput>
									</Td>
									<Td isNumeric>{row.last24h.toLocaleString('pt-BR')}</Td>
									<Td isNumeric>{row.totalSent.toLocaleString('pt-BR')}</Td>
									<Td textAlign='right'>
										{row.active ? null : <Badge colorScheme='red'>Inativo</Badge>}
										<Button
											size='xs'
											colorScheme='brand'
											ml={2}
											isDisabled={!dirty}
											onClick={() => save(row.userId)}
										>
											Salvar
										</Button>
									</Td>
								</Tr>
							);
						})}
					</Tbody>
				</Table>
			)}

			<Flex justify='flex-end' mt='16px'>
				<Button size='sm' variant='outline' onClick={() => dispatch(fetchLiveLeadClients())}>
					Atualizar
				</Button>
			</Flex>
		</Card>
	);
}
